import { Link, useFetcher } from '@remix-run/react'
import type { SerializeFrom } from '@remix-run/server-runtime'
import { ImageIcon } from 'lucide-react'
import { useContext, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { MediaFormatType } from 'prisma/generated/zod/inputTypeSchemas/MediaFormatSchema'
import { AspectRatioInput } from '~/components/AspectRatioInput'
import * as EmptyState from '~/components/EmptyState'
import { Media } from '~/components/Media/Media'
import {
  SlideDeck,
  SlideDeckContent,
  SlideDeckItem,
  SlideDeckStepper,
} from '~/components/SlideDeck'
import { MediaManagerContext } from '~/contexts'
import type {
  AmendableAttachmentType,
  MediaManagerContextIntentType,
} from '~/contexts/MediaManagerContext'
import { makeShopOwnPath } from '~/helpers/routes'
import { useOptionalSeller } from '~/hooks/data/use-seller'
import { useShop } from '~/hooks/data/use-shop'
import { useIsMounted } from '~/hooks/useIsMounted'
import { useTailwindBreakpoint } from '~/hooks/useTailwindBreakpoint'
import type { listAttachmentsOfTarget } from '~/models/attachment.server'
import { Button, TextEditable } from '~/ui'
import { cn } from '~/utils/classnames'

import { ShopSliderLink } from './shop-slider-link'

export type ShopSliderAttachments = SerializeFrom<
  Awaited<ReturnType<typeof listAttachmentsOfTarget>>['attachments']
>

export type ShopSliderProps = {
  attachments: ShopSliderAttachments
  className?: string
}

const ShopSlider = ({
  attachments: propAttachments,
  className,
}: ShopSliderProps) => {
  const { t } = useTranslation()
  const shop = useShop()
  const seller = useOptionalSeller()
  const fetcher = useFetcher()

  const [, setMediaManagerContext] = useContext(MediaManagerContext)

  const attachments = useMemo(() => {
    return propAttachments
      .filter((att) => att.media.src != '')
      .sort((a, b) => Number(a.disposition) - Number(b.disposition))
  }, [propAttachments])

  const [format, setFormat] = useState<MediaFormatType>(
    shop.mediaFormat || 'square'
  )

  const [currentIndex, setCurrentIndex] = useState(0)
  const isMounted = useIsMounted()
  const isDesktop = useTailwindBreakpoint('sm', false, () =>
    document.querySelector('#shop-container')
  )

  const mediaManagerContextInitial = {
    isOpen: true,
    intent: 'upload_attachments' as MediaManagerContextIntentType,
    maxSelected: 8,
    title: t('components.media_manager.shop_slider_title') || '',
    targetType: 'slider',
    targetId: null,
    attachments: attachments as unknown as AmendableAttachmentType[],
  }

  // placeholder
  if (attachments.length === 0) {
    return (
      <div className="bg-under/4 flex aspect-square h-full items-center justify-center overflow-hidden p-4 @xs:rounded-md">
        <div className="z-20 flex w-80 flex-col items-center gap-8">
          <div className="flex flex-col items-center gap-2">
            <EmptyState.Slider className="@max-sm:hidden" />
            <h1 className="text-foreground mt-6 text-center text-sm font-medium">
              {t('page.home.empty_state.title')}
            </h1>
            <h2 className="text-foreground/64 text-center text-sm font-medium">
              {t('page.home.empty_state.subtitle')}
            </h2>
          </div>
          <Button
            size="md"
            variant="over-media"
            onClick={() => setMediaManagerContext(mediaManagerContextInitial)}
          >
            <ImageIcon className="opacity-64" />
            {t('action.create_slider')}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className={cn('group relative', className)}>
      <SlideDeck
        length={attachments.length}
        format={format}
        control={isDesktop ? 'click' : 'drag'}
        onChange={setCurrentIndex}
        className={cn(
          'h-full w-full @sm:box-content @sm:w-auto @md:pr-(--pr)',
          'rounded-none @sm:rounded-md',
          !isMounted && 'bg-foreground/8 animate-pulse *:opacity-0!'
        )}
      >
        <SlideDeckContent>
          {attachments.map(({ id, title, description, media, link }, index) => {
            return (
              <SlideDeckItem key={`${id}-${index}`} className="relative">
                {/* media */}
                <div className="absolute inset-0 w-full">
                  <Media
                    className="absolute inset-0 h-full w-full bg-black object-cover"
                    src={media.src}
                    kind={media.kind}
                    loading="lazy"
                    transformation={{
                      width: 1920,
                      format: 'webp',
                      resizeMode: 'fill',
                    }}
                    sizes={{
                      xl: '50vw',
                      lg: '50vw',
                      md: '100vw',
                      sm: '100vw',
                    }}
                  />
                  <div
                    role="presentation"
                    aria-hidden="true"
                    inert
                    className="absolute top-1/3 right-0 bottom-0 left-0 bg-linear-to-t from-black/64 to-black/0"
                  />
                </div>

                {/* content */}
                <ConditionalItemLink
                  link={link && !seller ? makeShopOwnPath(shop, link) : null}
                  isActive={index === currentIndex}
                >
                  <div
                    className={cn(
                      'text-highlight absolute inset-0 flex flex-col items-center justify-end p-6 text-center',
                      'pointer-events-none *:pointer-events-auto',
                      !isDesktop && 'pb-12'
                    )}
                  >
                    <div className="flex w-full flex-col items-center gap-px text-sm text-white">
                      {seller && (
                        <div className="w-full max-w-xs">
                          {['title', 'description'].map((key) => (
                            <TextEditable
                              className="w-full [--color-accent-element:white]"
                              key={key}
                            >
                              <TextEditable.Textarea
                                placeholder={t(
                                  `input.shop_slider.${key}_placeholder`
                                )}
                                className={cn(
                                  'text-center font-medium opacity-70 placeholder:text-white',
                                  key === 'title' && 'font-medium',
                                  key === 'description' && 'opacity-70'
                                )}
                                defaultValue={
                                  (key === 'title' ? title : description) || ''
                                }
                                maxLength={key === 'title' ? 30 : 120}
                                preventLineBreaks
                                onBlur={(event) => {
                                  fetcher.submit(
                                    { [key]: event.currentTarget.value },
                                    {
                                      method: 'PATCH',
                                      action: makeShopOwnPath(
                                        shop,
                                        `/attachments/${id}`
                                      ),
                                    }
                                  )
                                }}
                              />
                            </TextEditable>
                          ))}
                          <ShopSliderLink
                            sliderItemId={id}
                            currentLink={link}
                          />
                        </div>
                      )}
                      {!seller && (
                        <div className="max-w-xs">
                          <h3>{title}</h3>
                          <p className="opacity-70">{description}</p>
                        </div>
                      )}
                    </div>

                    {isDesktop && <SlideDeckStepper className="mt-3" />}
                  </div>
                </ConditionalItemLink>
              </SlideDeckItem>
            )
          })}
        </SlideDeckContent>

        {/* edit attachments */}
        {seller && (
          <Button
            variant="over-media"
            size="md"
            className={cn(
              'absolute top-3 right-3 z-20',
              'overlay-hover-control'
            )}
            data-position="top-right"
            onClick={() => setMediaManagerContext(mediaManagerContextInitial)}
          >
            <ImageIcon />
            {t('action.edit_slider')}
          </Button>
        )}

        {/* overlay footer */}
        <div
          className={cn(
            'absolute -bottom-1 left-0 z-10 grid w-full grid-cols-[1fr_auto_1fr] justify-end p-3',
            'pointer-events-none *:pointer-events-auto'
          )}
        >
          {!isDesktop && <SlideDeckStepper className="col-start-2" />}
          {seller && (
            <div
              className={cn('pointer-events-auto col-start-3 ml-auto w-fit')}
            >
              <AspectRatioInput
                className="overlay-hover-control"
                data-position="bottom-right"
                value={format}
                onChange={(format) => {
                  setFormat(format)

                  fetcher.submit(
                    { mediaFormat: format },
                    {
                      method: 'patch',
                      action: makeShopOwnPath(shop, '/settings?index'),
                    }
                  )
                }}
              />
            </div>
          )}
        </div>
      </SlideDeck>
    </div>
  )
}

interface ConditionalItemLinkProps {
  link?: string | null
  children: React.ReactNode
  isActive?: boolean
}

const ConditionalItemLink = ({
  isActive,
  link,
  children,
}: ConditionalItemLinkProps) => {
  if (!link) return children

  return (
    <Link
      to={link}
      draggable={false}
      className="absolute inset-0"
      onClick={(event) => {
        if (!isActive) {
          return event.preventDefault()
        }

        const closestClickable =
          event.target instanceof Element
            ? event.target.closest('a,button,[role="button"]')
            : null

        if (closestClickable !== event.currentTarget) {
          event.preventDefault()
        }
      }}
    >
      {children}
    </Link>
  )
}

export { ShopSlider }
